import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { DialogComponent } from './dialog.component';
import { isPlatformBrowser } from '@angular/common';

export type DialogProps = {
  component: any;
  componentProps?: any;
  hasBackdrop?: boolean;
};

export type DialogDismissProps = {
  data: any;
  role: string | undefined;
};

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private modal: DialogRef | undefined;
  private id = signal<number>(0);
  private ids = signal<string[]>([]);
  private readonly platformId = inject(PLATFORM_ID);

  constructor(private dialog: Dialog) {}

  show(props: DialogProps): DialogRef | any {
    return this.create(DialogComponent, props);
  }

  create(component: ComponentType<any>, data: DialogProps): DialogRef | any {
    if (isPlatformBrowser(this.platformId)) {
      this.id.set(this.id() + 1);

      this.modal = this.dialog.open(component, {
        data,
        id: `modal-${this.id()}`,
        hasBackdrop: false,
      });

      this.ids().push(this.modal.id);

      return this.modal;
    }
  }

  dismiss(data?: any, role?: string): void {
    const top = this.getTop();
    const element = this.getElement();
    if (element) {
      const closeAnimation = element.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 200,
        easing: 'ease-in-out',
      });
      closeAnimation!.onfinish = () => {
        const result: DialogDismissProps = { data: undefined, role: undefined };
        if (data) result.data = data;
        if (role) result.role = role;
        if (top) top.close(result);
        this.ids().pop();
      };
    }
  }

  getTop() {
    const dialogs = document.querySelectorAll('cdk-dialog-container');
    const topId = dialogs.item(dialogs.length - 1)?.id;
    if (topId) {
      const top = this.dialog.getDialogById(topId);
      return top;
    }

    return null;
  }

  getElement(): HTMLElement | null {
    const dialogs = document.querySelectorAll('cdk-dialog-container');
    const topId = dialogs.item(dialogs.length - 1)?.id;
    if (topId) {
      return document.getElementById(topId);
    }

    return null;
  }
}
